import {Trans} from '@lingui/macro';
import _ from 'lodash';
import React, {FC, useMemo, useState} from 'react';

import {AsyncHandlerOf} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {IconButton} from '../../../libs/shared/ui/src/components/buttons/iconButton';
import {useUserTheme} from '../../../libs/shared/ui/src/theme/userThemeProvider';
import {openInBrowser} from '../../../srcCommon/browser/openInBrowser';
import {useConstant, useToggle} from '../../../srcCommon/helpers/hookHelpers';
import {useLinkNavigation} from '../../components/common/navigation/useLinkNavigation';
import {isTeamNamespace} from '../../helpers/models/namespaceHelpers';
import {useAppDispatch, useAppSelector} from '../../helpers/reduxHelpers';
import {SettingsCustomSnoozeModel} from '../../models/api/customSnoozeModel';
import {buildSettingsPath} from '../../models/url/settingsUrlHelpers';
import {SettingsResourcePagesEnum, SettingsSnoozesUrl} from '../../models/url/settingsUrlModel';
import {ResourceMap} from '../../store/appTypes';
import {dismissSettingsInfoBoxAction} from '../../store/data/localPreferences/localPreferencesActions';
import {getFindShouldDisplayInfoBox} from '../../store/data/localPreferences/localPreferencesSelectors';
import {SettingsContentHeader} from '../common/settingsContentHeader';
import {
  StyledSettingsContent,
  StyledSettingsMain,
  StyledSettingsSidebar,
  StyledSettingsWrapper,
} from '../common/settingsWrapper';
import {SettingsHeader} from '../settingsHeader';
import {SettingsSnoozesSidebar} from './settingsSnoozesSidebar';
import {SettingsSnoozesTable} from './settingsSnoozesTable';

/*
 * Props.
 */

interface SettingsSnoozesListProps {
  snoozes: ResourceMap<SettingsCustomSnoozeModel>;
  snoozesUrl: SettingsSnoozesUrl;
  onSingleDeleteClick: AsyncHandlerOf<SettingsCustomSnoozeModel>;
  onBulkDeleteClick: AsyncHandlerOf<ReadonlyArray<number>>;
}

/*
 * Component.
 */

export const SettingsSnoozesList: FC<SettingsSnoozesListProps> = (props) => {
  const theme = useUserTheme();
  const {snoozesUrl} = props;
  const [searchQuery, setSearchQuery] = useState('');
  const {navigateToLink} = useLinkNavigation();
  const dispatch = useAppDispatch();
  const findShouldDisplayInfoBox = useAppSelector(getFindShouldDisplayInfoBox);
  // Sidebar state.
  const infoBoxId = useConstant(() =>
    isTeamNamespace(snoozesUrl.namespace) ? 'custom_snoozes_admin' : 'custom_snoozes_user',
  );
  const [isSidebarOpen, toggleSidebar] = useToggle(findShouldDisplayInfoBox(infoBoxId));
  const onInfoBoxCloseClick = () => {
    dispatch(dismissSettingsInfoBoxAction(infoBoxId));
    toggleSidebar();
  };

  const onSearchQueryChange = (newQuery: string) => {
    setSearchQuery(newQuery);
  };
  const snoozesIds = useMemo(
    () =>
      _(props.snoozes)
        .values()
        .compact()
        .filter((s) => s?.name.toLowerCase().includes(searchQuery.toLowerCase()))
        .map((s) => s?.id)
        .compact()
        .value(),
    [props.snoozes, searchQuery],
  );

  const renderHeaderButton = (buttonProps: React.ComponentProps<typeof Button>) => (
    <Button
      {...buttonProps}
      variant={ButtonVariantsEnum.PRIMARY}
      onClick={() =>
        navigateToLink(buildSettingsPath({...snoozesUrl, resourcePage: SettingsResourcePagesEnum.NEW}))
      }
    >
      <Trans id="settings-snoozes-addasnooze">Add a snooze</Trans>
    </Button>
  );
  return (
    <StyledSettingsWrapper>
      <SettingsHeader
        right={
          <IconButton
            iconName={isSidebarOpen ? 'infoFilled' : 'info'}
            label=""
            onClick={toggleSidebar}
            color={isSidebarOpen ? theme.palette.blue.shade40 : theme.greys.shade70}
            hoverColor={isSidebarOpen ? theme.palette.blue.shade40 : theme.greys.shade70}
          />
        }
      >
        <Trans id="settings-snoozes-header">Snoozes</Trans>
      </SettingsHeader>
      <StyledSettingsMain>
        <StyledSettingsContent>
          <SettingsContentHeader
            searchInputValue={searchQuery}
            onSearchInput={onSearchQueryChange}
            onExitClick={() => {
              setSearchQuery('');
            }}
            renderButtons={renderHeaderButton}
          />
          <SettingsSnoozesTable
            snoozesIds={snoozesIds}
            snoozes={props.snoozes}
            snoozesUrl={props.snoozesUrl}
            onSingleDeleteClick={props.onSingleDeleteClick}
            onBulkDeleteClick={props.onBulkDeleteClick}
          />
        </StyledSettingsContent>
        {isSidebarOpen && (
          <StyledSettingsSidebar>
            <SettingsSnoozesSidebar
              namespace={snoozesUrl.namespace}
              onCloseClick={onInfoBoxCloseClick}
              onFooterClick={() => {
                openInBrowser(
                  'https://help.front.com/t/x124v7/how-to-snooze-conversations-for-later-or-follow-up',
                );
              }}
            />
          </StyledSettingsSidebar>
        )}
      </StyledSettingsMain>
    </StyledSettingsWrapper>
  );
};
